import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  assignChatAdmins,
  deleteChatMessage,
  editChatMessage,
  markChatRead,
  markChatUnread,
  removeChatReaction,
  sendChatMessage,
  sendFileMessage,
  sendVoiceMessage,
  setChatReaction,
} from '../../../api/chats';
import { useAuth } from '../../auth/AuthProvider';
import type { ChatMessage, PatientChatThread } from '../../../types/admin';

export function useSendChatMessage(patientId: string) {
  const queryClient = useQueryClient();
  const { tokens, user } = useAuth();

  return useMutation({
    mutationFn: async (messageText: string) => {
      if (!tokens?.access_token || !user) {
        throw new Error('Authenticated admin session is required.');
      }
      return sendChatMessage(
        patientId,
        { message_text: messageText },
        tokens.access_token,
        { full_name: user.full_name, id: user.id },
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chat', patientId] }),
      ]);
    },
  });
}

export function useSendVoiceMessage(patientId: string) {
  const queryClient = useQueryClient();
  const { tokens, user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      readonly file: Blob;
      readonly duration_ms: number;
      readonly waveform?: readonly number[];
      readonly filename?: string;
    }) => {
      if (!tokens?.access_token || !user) {
        throw new Error('Authenticated admin session is required.');
      }
      return sendVoiceMessage(patientId, input, tokens.access_token, {
        full_name: user.full_name,
        id: user.id,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chat', patientId] }),
      ]);
    },
  });
}

export function useSendFileMessage(patientId: string) {
  const queryClient = useQueryClient();
  const { tokens, user } = useAuth();

  return useMutation({
    mutationFn: async (input: { readonly file: File; readonly filename?: string }) => {
      if (!tokens?.access_token || !user) {
        throw new Error('Authenticated admin session is required.');
      }
      return sendFileMessage(patientId, input, tokens.access_token, {
        full_name: user.full_name,
        id: user.id,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chat', patientId] }),
      ]);
    },
  });
}

export function useEditChatMessage(patientId: string) {
  const queryClient = useQueryClient();
  const { tokens } = useAuth();

  return useMutation({
    mutationFn: async (input: { readonly messageId: string; readonly messageText: string }) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return editChatMessage(
        patientId,
        input.messageId,
        { message_text: input.messageText },
        tokens.access_token,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chat', patientId] }),
      ]);
    },
  });
}

export function useDeleteChatMessage(patientId: string) {
  const queryClient = useQueryClient();
  const { tokens } = useAuth();

  return useMutation({
    mutationFn: async (messageId: string) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      await deleteChatMessage(patientId, messageId, tokens.access_token);
      return messageId;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chat', patientId] }),
      ]);
    },
  });
}

export function useChatReaction(patientId: string) {
  const queryClient = useQueryClient();
  const { tokens } = useAuth();

  return useMutation({
    mutationFn: async (input: { readonly messageId: string; readonly emoji: string; readonly remove: boolean }) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return input.remove
        ? removeChatReaction(patientId, input.messageId, input.emoji, tokens.access_token)
        : setChatReaction(patientId, input.messageId, input.emoji, tokens.access_token);
    },
    onMutate: (input): { readonly original: ChatMessage | undefined } => ({
      original: queryClient.getQueryData<PatientChatThread | null>(['patient-chat', patientId])
        ?.messages.find((message) => message.id === input.messageId),
    }),
    onSuccess: (updated, _input, context) => {
      queryClient.setQueryData<PatientChatThread | null>(['patient-chat', patientId], (current) => {
        if (!current || !context?.original) return current;
        // A realtime update replaced this object while request was in flight.
        // Keep that newer state instead of writing a delayed REST snapshot.
        if (current.messages.find((message) => message.id === updated.id) !== context.original) {
          return current;
        }
        return {
          ...current,
          messages: current.messages.map((message) => message.id === updated.id ? updated : message),
        };
      });
    },
  });
}

export function useMarkChatRead(patientId: string) {
  const queryClient = useQueryClient();
  const { tokens } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return markChatRead(patientId, tokens.access_token);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chat', patientId] }),
      ]);
    },
  });
}

export function useMarkChatUnread() {
  const queryClient = useQueryClient();
  const { tokens } = useAuth();

  return useMutation({
    mutationFn: async (patientId: string) => {
      if (!tokens?.access_token) throw new Error('Authenticated admin session is required.');
      await markChatUnread(patientId, tokens.access_token);
      return patientId;
    },
    onSuccess: async (patientId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chat', patientId] }),
      ]);
    },
  });
}

export function useAssignChatAdmins(patientId: string) {
  const queryClient = useQueryClient();
  const { tokens } = useAuth();

  return useMutation({
    mutationFn: async (adminIds: readonly string[]) => {
      if (!tokens?.access_token) {
        throw new Error('Authenticated admin session is required.');
      }
      return assignChatAdmins(patientId, { admin_ids: adminIds }, tokens.access_token);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-chats'] }),
        queryClient.invalidateQueries({ queryKey: ['patient-chat', patientId] }),
      ]);
    },
  });
}
